import { AppDebug } from "../debug/AppDebug";
import { Localization, LocalizationEvents } from "./Localization";
import { AppLocalStorage } from "../managers/AppLocalStorage";
import { CardDeckData, OptionsItemLiveData, SharedLocData, VariationLocData } from "../../types/types";
import { VariationId } from "../screen/TableScreen";
import { ChangedItemData, LiveDataChangeEvents, OptionsDataManager } from "../managers/OptionsDataManager";

import { OptionsItemKeys } from "../managers/LayoutDataManager";

// shared loc
import shared_loc_en from "../data/shared/shared_loc_en.json";
import shared_loc_fr from "../data/shared/shared_loc_fr.json";
import shared_loc_de from "../data/shared/shared_loc_de.json";
import shared_loc_sp from "../data/shared/shared_loc_sp.json";
// import shared_loc_por from "../data/shared/shared_loc_por.json";

// belote loc
import belote_loc_en from "../data/belote/belote_loc_en.json";
import belote_loc_fr from "../data/belote/belote_loc_fr.json";
import belote_loc_de from "../data/belote/belote_loc_de.json";
import belote_loc_sp from "../data/belote/belote_loc_sp.json";
// import belote_loc_por from "../data/belote/belote_loc_por.json";

// coinche loc
import coinche_loc_en from "../data/coinche/coinche_loc_en.json";
import coinche_loc_fr from "../data/coinche/coinche_loc_fr.json";
import coinche_loc_de from "../data/coinche/coinche_loc_de.json";
import coinche_loc_sp from "../data/coinche/coinche_loc_sp.json";
// import coinche_loc_por from "../data/coinche/coinche_loc_por.json";

// contree loc
import contree_loc_en from "../data/contree/contree_loc_en.json";
import contree_loc_fr from "../data/contree/contree_loc_fr.json";
import contree_loc_de from "../data/contree/contree_loc_de.json";
import contree_loc_sp from "../data/contree/contree_loc_sp.json";
// import contree_loc_por from "../data/contree/contree_loc_por.json";

// cardDeck-specific (contains textual data displayed basedon which card deck is selected [not which language])
import card_decks_data from "../data/card_decks_data.json";


export enum AppLocalizationEvents {
    // for now we have this here..
    VARIATION_CHANGED = "variation_changed",
}

/**
 * Must be same as in @see gameSettings.languages settings file.
 */
export enum LanguageId {
    ENGLISH = 0,
    FRENCH = 1,
    GERMAN = 2,
    SPANISH = 3,
    // PORTUGUESE = 4,
}

/**
 * App-specific Localization, extends @see Localization base 
 * 
 * In French Belote, there are:
 * 1) 'shared loc' lang files, one for all variations one per each language
 *    total count: 1 * langs_count
 *    access: @see GetShared(): SharedLocData
 * 2) 'variation loc' lang files, one per each variation, per each language
 *    total count: variations_count * langs_count
 *    access: @see GetByVariation(variationId:VariationId)
 */
export class AppLocalization extends Localization {
    /**
     * Stores shared loc data by LanguageId
     */
    protected static sharedLocs: SharedLocData[];

    /**
     * Stores variation loc data by Variation and LanguageId
     * first key: VariationId, second key: LanguageId
     */
    protected static variationLocs: [][];

    /**
     * Language to pick, if everything else fails (browser returns unsupported locale, or whatever)
     */
    protected static defaultLanguage: LanguageId = LanguageId.ENGLISH;

    /**
     * A language 'index', that corresponds to language selected by the user, in the app,
     * or a default language.
     * 
     * NOTE: First time launch (no cache) sets this from locale string reported by browser, 
     *       if such locale can be matched to existing @see LanguageId key 
     *       via @see this.localeStringsToLanguageIndex
     */
    protected static curLanguageId: LanguageId;

    /**
     * Current variation index for @see GetShared without supplying argument
     */
    protected static curVariationId: VariationId;


    /**
     * Codes from @see http://www.lingoes.net/en/translator/langcode.htm
     */
    public static override Init(): void {
        // cache browser locales in @see this.navigatorLocales
        super.Init();

        // cache locales->language translation tables
        this.localeStringsToLanguageIndex = [];

        // ENGLISH
        this.SetLocaleStringsToLanguage(
            ["en", "en-AU", "en-BZ", "en-CA", "en-CB", "en-GB", "en-IE", "en-JM", "en-NZ", "en-PH", "en-TT", "en-US", "en-ZA", "en-ZW"],
            LanguageId.ENGLISH);

        // FRENCH
        this.SetLocaleStringsToLanguage(
            ["fr", "fr-BE", "fr-CA", "fr-CH", "fr-FR", "fr-LU", "fr-MC"],
            LanguageId.FRENCH);

        // GERMAN
        this.SetLocaleStringsToLanguage(
            ["de", "de-CH", "de-DE", "de-LI", "de-LU"],
            LanguageId.GERMAN);

        // SPANISH
        this.SetLocaleStringsToLanguage(
            ["es", "es-AR", "es-BO", "es-CL", "es-CO", "es-CR", "es-DO", "es-EC", "es-ES", "es-GT", "es-HN", "es-MX", "es-NI", "es-PA", "es-PE", "es-PR", "es-PY", "es-SV", "es-UY", "es-VE"],
            LanguageId.SPANISH);

        // PORTUGESE
        // this.SetLocaleStringsToLanguage(["pt", "pt-BR", "pt-PT"],
        //     LanguageId.PORTUGUESE);

        // ITALIAN
        // this.SetLocaleStringsToLanguage(
        //     ["it", "it-CH", "it-IT"],
        //     LanguageId.ITALIAN);

        // language index to language file
        this.sharedLocs = [];
        this.sharedLocs[LanguageId.ENGLISH] = shared_loc_en;
        this.sharedLocs[LanguageId.FRENCH] = shared_loc_fr;
        this.sharedLocs[LanguageId.GERMAN] = shared_loc_de;
        this.sharedLocs[LanguageId.SPANISH] = shared_loc_sp;
        // this.sharedLocs[LanguageId.PORTUGUESE] = shared_loc_por;

        /**
         * 
         *   LANGUAGE DETECTION
         * 
         */

        const debugLanguageIndex: number = AppDebug.GetLanguageIndex();
        let applicationLanguage: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.applicationLanguage);
        const applicationLanguageIndex: number = applicationLanguage ? applicationLanguage.value as number : -1;
        const localeLanguageIndex: number = this.GetLanguageIndexFromNavigatorLocale();

        // if 'lang' was supplied through get parameter
        // - lang from GET params has highest priority (even higher than cached language)
        if (debugLanguageIndex != -1) {
            this.curLanguageId = debugLanguageIndex;
            // use for this session only (not to localStorage)
            applicationLanguage.value = debugLanguageIndex;
        }
        // if got cached language in localstorage
        else if (applicationLanguageIndex != -1) {
            // use cached language
            this.curLanguageId = applicationLanguageIndex;
        }
        // if navigator locale language fetch succeeded
        else if (localeLanguageIndex != -1) {
            // console.log("navigator language (detected):", LanguageId[localeLanguageIndex]);
            // use langauge from navigator locale
            this.curLanguageId = localeLanguageIndex;
            // save it
            applicationLanguage.value = localeLanguageIndex;
            OptionsDataManager.SaveSharedData();
        }

        // if all else fails, set default
        // NOTE: this can be done, because everything else in the game runs 'after this detection'.
        if (this.curLanguageId == undefined) {
            // console.log("language autodetection failed. Using default language:", LanguageId[this.defaultLanguage]);
            // assign default language ENGLISH as current language
            this.curLanguageId = this.defaultLanguage;
            // save it
            applicationLanguage.value = this.defaultLanguage;
            OptionsDataManager.SaveSharedData();
            // console.log("Lang set from defaults to", LanguageId[this.curLanguage]);
        }

        // listen to language change
        OptionsDataManager.GetEventEmitter().on(LiveDataChangeEvents.LANGUAGE_CHANGE, this.OnLanguageDataChange, this);
    }

    private static OnLanguageDataChange(itemData: ChangedItemData): void {
        const languageId: LanguageId = itemData.live.value as number;
        this.SetCurLanguageId(languageId);
    }

    /**
     * Externally set language by language index
     * NOTE: Indexes of language buttons in SYSTEM category of @see PopupWindowTheming
     *       correspond DIRECTLY to the languages defined here and their data models
     * @param newLanguage 
     */
    public static SetCurLanguageId(newLanguageId: LanguageId, emitChange: boolean = true): void {
        if (this.curLanguageId != newLanguageId) {
            this.curLanguageId = newLanguageId;
            if (emitChange) {
                // all app content should react to this change in language event
                this.eventEmitter.emit(LocalizationEvents.LANGUAGE_CHANGED, newLanguageId);
            }
        }
    }

    public static GetCurLanguageId(): LanguageId {
        return this.curLanguageId;
    }

    public static SetCurVariationId(newVariationId: VariationId): void {
        if (this.curVariationId != newVariationId) {
            this.curVariationId = newVariationId;
            this.eventEmitter.emit(AppLocalizationEvents.VARIATION_CHANGED);
        }
    }

    public static GetCurVariationId(): VariationId {
        return this.curVariationId;
    }

    /**
     * Returns shared localization.
     * NOTE: to get variation's localization @see GetVariationData
     * 
     * @param langId optional. If specificed, returns shared loc for langId language, otherwise returns shared loc for @see this.curLanguageId language
     * @returns shared loc data
     */
    public static GetSharedData(langId: LanguageId = undefined): SharedLocData {
        // console.log("GetShared", langId, this.curLanguageId);

        if (langId == undefined) {
            langId = this.curLanguageId;
        }
        switch (langId) {
            case LanguageId.ENGLISH:
                return shared_loc_en;
            case LanguageId.FRENCH:
                return shared_loc_fr;
            case LanguageId.GERMAN:
                return shared_loc_de;
            case LanguageId.SPANISH:
                return shared_loc_sp;
            // case LanguageId.PORTUGUESE:
            //     return shared_loc_por;
        }
    }

    /**
     * Returns variation's localization.
     * NOTE: to get shared localization @see GetShared
     * 
     * @param variationId optional. If specificed, returns loc for variationId variation, otherwise returns loc for @see this.curVariationId variation.
     * @param langId optional. If specificed, returns loc for langId language, otherwise returns loc for @see this.curLanguageId language.
     * @returns 
     */
    public static GetVariationData(
        variationId: VariationId = undefined,
        langId: LanguageId = undefined,
    ): VariationLocData {
        // console.log(`GetVariationData ${variationId}(${this.curVariationId}), ${langId}(${this.curLanguageId})`);

        if (variationId == undefined) {
            variationId = this.curVariationId;
        }
        if (langId == undefined) {
            langId = this.curLanguageId;
        }
        //  console.log(VariationId[variationId], LanguageId[langId]);

        switch (variationId) {
            case VariationId.BELOTE:
                switch (langId) {
                    case LanguageId.ENGLISH:
                        return belote_loc_en;
                    case LanguageId.FRENCH:
                        return belote_loc_fr;
                    case LanguageId.GERMAN:
                        return belote_loc_de;
                    case LanguageId.SPANISH:
                        return belote_loc_sp;
                    // case LanguageId.PORTUGUESE:
                    //     return belote_loc_por;
                }

            case VariationId.COINCHE:
                switch (langId) {
                    case LanguageId.ENGLISH:
                        return coinche_loc_en;
                    case LanguageId.FRENCH:
                        return coinche_loc_fr;
                    case LanguageId.GERMAN:
                        return coinche_loc_de;
                    case LanguageId.SPANISH:
                        return coinche_loc_sp;
                    // case LanguageId.PORTUGUESE:
                    //     return coinche_loc_por;
                }
            case VariationId.CONTREE:
                switch (langId) {
                    case LanguageId.ENGLISH:
                        return contree_loc_en;
                    case LanguageId.FRENCH:
                        return contree_loc_fr;
                    case LanguageId.GERMAN:
                        return contree_loc_de;
                    case LanguageId.SPANISH:
                        return contree_loc_sp;
                    // case LanguageId.PORTUGUESE:
                    //     return contree_loc_por;
                }
        }
    }

    public static GetCardDeckData(cardDeckIndex: number): CardDeckData {
        return card_decks_data[cardDeckIndex];
    }

    public static GetHintTextById(key: string): string {
        return this.GetSharedData(this.curLanguageId).table.hints.itemsById[key];
    }

    /**
     * language index from navigator, or -1 if not found
     */
    private static GetLanguageIndexFromNavigatorLocale(): number {
        // get locale from navigator
        for (let i: number = 0; i < this.navigatorLocales.length; i++) {
            const locale: string = this.navigatorLocales[i];
            // console.log(locale, this.localeStringsToLanguageIndex[locale]);
            // if such language is supported (we got translation)
            if (this.localeStringsToLanguageIndex[locale] != undefined) {
                return this.localeStringsToLanguageIndex[locale];
            }
        }
        return -1;
    }

    /**
     * 
     * @param localeStrings 
     * @param language 
     */
    protected static SetLocaleStringsToLanguage(localeStrings: string[], language: LanguageId): void {
        for (let i: number = 0; i < localeStrings.length; i++) {
            this.localeStringsToLanguageIndex[localeStrings[i]] = language;
        }
    }
}